import {
  AutoCompleteController,
  Button,
  InputController,
} from '../../../components';
import { useAppDispatch } from '../../../storeHook';
import { showAlert } from '../../../services/alert';
import * as yup from 'yup';
import { useFormWithSchema } from '../../../utils/functions/yupFormTypescript';
import {
  useNavigate,
  useSearchParams,
} from 'react-router-dom';
import {
  useGetCommissionByIdQuery,
  useUpdateCommissionMutation,
} from '../../../services/commission';
import { skipToken } from '@reduxjs/toolkit/query';
import { useEffect } from 'react';
import * as regex from '../../../utils/constants/regex';
import { useApiHelper } from './helpers/apiHelper';

const formSchema = yup.object({
  enterpriseId: yup.number().required(),
  plant: yup.string().required(),
  matnr: yup.string().required(),
  commissionPerc: yup
    .string()
    .matches(
      regex.NUMBER_UPTO_TWO_DECIMAL_PLACES,
      'please enter a number upto two dacimal places'
    )
    .required('commission % is required'),
  productName: yup.string().required(),
});
type TFormData = yup.InferType<typeof formSchema>;

export const EditCommission: React.FC = () => {
  const navigate = useNavigate();
  const {
    formState: { errors },
    handleSubmit,
    register,
    control,
    reset,
    watch,
  } = useFormWithSchema(formSchema, {
    mode: 'onTouched',
  });

  const [searchParams] = useSearchParams();
  const commissionId = searchParams.get('id');
  const { data: fetchedData } = useGetCommissionByIdQuery(
    commissionId ?? skipToken
  );
  useEffect(() => {
    if (fetchedData) {
      reset({
        enterpriseId: fetchedData.enterpriseId,
        plant: fetchedData.plant,
        matnr: fetchedData.matnr,
        commissionPerc:
          fetchedData.commissionPerc.toString(),
        productName: fetchedData.productName,
      });
    }
  }, [fetchedData]);

  const dispatch = useAppDispatch();
  const [update] = useUpdateCommissionMutation();
  const onUpdateClick = (data: TFormData) => {
    fetchedData &&
      update({
        id: fetchedData.id,
        isDeleted: 0,
        ...data,
      })
        .unwrap()
        .then(() => {
          navigate('/commission');
          dispatch(
            showAlert({
              content: 'Commission Updated Successfully',
            })
          );
        })
        .catch((err) => {
          console.log('error', err);
          // this.utility.handleError(err);
        });
  };

  const {
    formattedListOfEnterprises,
    formattedListOfDepots,
    formattedListOfProducts,
  } = useApiHelper(watch('enterpriseId'));

  return (
    <form
      onSubmit={handleSubmit((data) => onUpdateClick(data))}
      className="grid grid-cols-2 gap-x-10">
      <AutoCompleteController
        label="Company"
        name="enterpriseId"
        control={control}
        options={formattedListOfEnterprises}
        error={errors.enterpriseId?.message}
        required
        disabled={!!commissionId}
      />
      <AutoCompleteController
        label="Plants"
        name="plant"
        control={control}
        options={formattedListOfDepots}
        error={errors.plant?.message}
        required
        disabled={!!commissionId}
      />
      <AutoCompleteController
        label="MATNR"
        name="matnr"
        control={control}
        options={formattedListOfProducts}
        error={errors.matnr?.message}
        required
        disabled={!!commissionId}
      />
      <InputController
        label="Commission %"
        register={register('commissionPerc')}
        error={errors.commissionPerc?.message}
        required
      />
      <InputController
        label="Product Name"
        register={register('productName')}
        error={errors.productName?.message}
        required
        disabled={!!commissionId}
      />

      <div className="col-span-full form-control mt-8 flex justify-end">
        <Button
          type="button"
          variant="button--secondary"
          onClick={() => navigate('/commission')}>
          Cancel
        </Button>
        <Button className="ml-3" type="submit">
          {commissionId ? 'Update' : 'Save'}
        </Button>
      </div>
    </form>
  );
};
